import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
    { id: "about", label: "Manufacturer" },
    { id: "products", label: "Range" },
    { id: "quality", label: "Quality" },
    { id: "contact", label: "Contact" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="main-nav"
            className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
                scrolled
                    ? "border-white/10 bg-obsidian/85 backdrop-blur-md"
                    : "border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-8">
                <Logo onClick={() => go("hero")} />
                <nav className="hidden items-center gap-8 md:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="group relative font-mono text-[11px] uppercase tracking-[0.25em] text-slate-300 transition-colors hover:text-amber-400"
                        >
                            {l.label}
                            <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-amber-500 transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                    <button
                        data-testid="nav-quote-btn"
                        onClick={() => go("contact")}
                        className="bg-tactical px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-slate-100 hover:-translate-y-0.5"
                    >
                        Request a Quote
                    </button>
                </nav>
                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 border border-steel md:hidden"
                    aria-label="Toggle menu"
                >
                    <span
                        className={`h-px w-5 bg-slate-200 transition-transform duration-300 ${open ? "translate-y-[3.5px] rotate-45" : ""}`}
                    />
                    <span
                        className={`h-px w-5 bg-slate-200 transition-transform duration-300 ${open ? "-translate-y-[3px] -rotate-45" : ""}`}
                    />
                </button>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden border-t border-white/10 bg-obsidian/95 backdrop-blur-md md:hidden"
                    >
                        <div className="flex flex-col px-6 py-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`nav-mobile-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="border-b border-white/5 py-4 text-left font-display text-2xl font-bold uppercase tracking-wide text-slate-200"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="nav-mobile-quote-btn"
                                onClick={() => go("contact")}
                                className="my-5 bg-tactical px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian"
                            >
                                Request a Quote
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
